import { EditIcon, ViewIcon } from '@chakra-ui/icons'
import { Box, Collapse, Flex, IconButton, Text } from '@chakra-ui/react'
import {
  capitalizeFirstLetter,
  CoverageId,
  FieldMapValue,
  InsuranceCoverage,
  insurersCollection,
} from '@hb/shared'
import { InsuranceCoveragePreview, useFormWizard } from '@hb/shared-frontend'
import { useCollection } from '@hb/shared-frontend/hooks/backend'
import { useCallback, useMemo } from 'react'
import { useInsuranceInfoStage } from './context'
import { InsuranceInfoForm } from './InsuranceInfoForm'
import { PlanIsMedicaid } from './PlanIsMedicaid'

const CoverageComplete = ({ label, isComplete }: { label: CoverageId; isComplete: boolean }) => {
  const { secondaryCoverage, primaryCoverage } = useInsuranceInfoStage()
  const { readOnly } = useFormWizard()
  const { items: insuranceProviders } = useCollection(insurersCollection)
  const coverageStatus = useMemo(() => {
    if (label === 'primary') {
      return primaryCoverage
    }
    return secondaryCoverage
  }, [label, primaryCoverage, secondaryCoverage])
  const { onEdit } = coverageStatus

  return (
    <Collapse in={isComplete}>
      <Flex w="100%" bg="white" p={2} borderRadius={4} border="1px solid" borderColor="gray.200">
        <Box flex={1}>
          <InsuranceCoveragePreview
            plan={coverageStatus.data as InsuranceCoverage}
            allInsuranceProviders={insuranceProviders}
            size="lg"
            noBadge
          />
        </Box>
        <IconButton
          variant="ghost"
          borderRadius="full"
          onClick={onEdit}
          icon={readOnly ? <ViewIcon /> : <EditIcon />}
          aria-label="Edit"
        />
      </Flex>
    </Collapse>
  )
}

// const MakePrimaryButton = () => {
//   const { insuranceInfoData, onSaveChanges } = useInsuranceInfoStage()

//   const [isLoading, setIsLoading] = useState(false)

//   const handleClick = useCallback(async () => {
//     setIsLoading(true)
//     const secondaryCoverage = insuranceInfoData?.secondaryCoverage
//     const primaryCoverage = insuranceInfoData?.primaryCoverage
//     if (!secondaryCoverage || !primaryCoverage) {
//       return
//     }
//     try {
//       await onSaveChanges({
//         primaryCoverage: { ...secondaryCoverage },
//         secondaryCoverage: { ...primaryCoverage },
//         // primaryIsMedicaid: !!insuranceInfoData?.secondaryIsMedicaid,
//         // secondaryIsMedicaid: !!insuranceInfoData?.primaryIsMedicaid,
//       })
//     } catch (e) {
//       console.error(e)
//     }
//     setIsLoading(false)
//   }, [onSaveChanges, insuranceInfoData])

//   return (
//     <ActionButton isLoading={isLoading} size="xs" onClick={handleClick}>
//       Make Primary
//     </ActionButton>
//   )
// }

const CoverageSelect = ({ label }: { label: CoverageId }) => {
  const { onUpdate, secondaryCoverage, primaryCoverage } = useInsuranceInfoStage()

  const { readOnly } = useFormWizard()
  const coverageStatus = useMemo(() => {
    if (label === 'primary') {
      return primaryCoverage
    }
    return secondaryCoverage
  }, [label, primaryCoverage, secondaryCoverage])

  const {
    hasCoverage: hasPrimaryCoverage,
    errors: primaryCoverageErrors,
    data: primaryCoverageData,
    complete: primaryCoverageComplete,
  } = primaryCoverage
  const { hasCoverage, field, type, data, complete, errors, onCancel } = coverageStatus

  const handleSubmit = useCallback(
    (d: FieldMapValue) => {
      const submitted = {
        [label === 'primary' ? 'primaryCoverage' : 'secondaryCoverage']: {
          ...d,
          isMedicaid: type === 'medicaid',
        },
      }
      if (submitted.memberId && submitted.noMemberId) delete submitted.noMemberId
      return onUpdate(submitted, true, true)
    },
    [onUpdate, label, type],
  )

  const isActive = useMemo(() => {
    if (label === 'secondary') {
      return (
        hasCoverage &&
        hasPrimaryCoverage &&
        (readOnly || (primaryCoverageData && primaryCoverageComplete && !primaryCoverageErrors))
      )
    }
    return hasPrimaryCoverage
  }, [
    label,
    hasCoverage,
    hasPrimaryCoverage,
    readOnly,
    primaryCoverageErrors,
    primaryCoverageData,
    primaryCoverageComplete,
  ])

  const isComplete = useMemo(
    () => complete && hasCoverage && !errors,
    [complete, hasCoverage, errors],
  )

  return (
    <Collapse in={isActive}>
      <Flex w="100%" flexFlow="column">
        <Flex pt={2} align="center" width="100%" justifyContent="space-between">
          <Text fontWeight={500} fontFamily="Hero-New">
            {capitalizeFirstLetter(label)} Coverage
          </Text>
          {/* {label === 'secondary' && isComplete ? <MakePrimaryButton /> : null} */}
        </Flex>
        <PlanIsMedicaid isComplete={isComplete} label={label} />
        <InsuranceInfoForm
          field={field}
          data={data}
          onSubmit={handleSubmit}
          isActive={!complete && type !== null}
          onCancel={onCancel}
        />
        <CoverageComplete isComplete={isComplete} label={label} />
      </Flex>
    </Collapse>
  )
}

export const PrimaryCoverageSelect = () => <CoverageSelect label="primary" />
export const SecondaryCoverageSelect = () => <CoverageSelect label="secondary" />
